import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { usePartidas } from '@/hooks/usePartidas';
import { useCrearSubPartida } from '@/hooks/useSubPartidas';
import type { SubPartidaCrear, TipoCasa } from '@adelante/shared';
import { CircleNotch } from '@phosphor-icons/react';
import { useState } from 'react';
import { toast } from 'sonner';

const TIPOS_CASA: TipoCasa[] = ['1N-Techo', '1N-Azotea', '2N-Techo', '2N-Azotea'];

interface Props {
  abierto: boolean;
  onClose: () => void;
}

interface FormState {
  codigo: string;
  nombre: string;
  partida_id: string;
  sprint_numero: string;
  es_critica: boolean;
  descripcion: string;
  activo: boolean;
  tipos_casa: Set<TipoCasa>;
}

const FORM_VACIO: FormState = {
  codigo: '',
  nombre: '',
  partida_id: '',
  sprint_numero: '',
  es_critica: false,
  descripcion: '',
  activo: true,
  tipos_casa: new Set(),
};

export function CrearSubPartidaDialog({ abierto, onClose }: Props) {
  const partidasQ = usePartidas();
  const mutation = useCrearSubPartida();
  const [form, setForm] = useState<FormState>(FORM_VACIO);

  const partidas = partidasQ.data?.data ?? [];

  function handleClose(open: boolean) {
    if (!open && !mutation.isPending) {
      onClose();
      setForm(FORM_VACIO);
    }
  }

  function toggleTipoCasa(tc: TipoCasa) {
    const next = new Set(form.tipos_casa);
    if (next.has(tc)) next.delete(tc);
    else next.add(tc);
    setForm({ ...form, tipos_casa: next });
  }

  const codigoOk = form.codigo.trim().length > 0;
  const nombreOk = form.nombre.trim().length > 0;
  const partidaOk = form.partida_id !== '';
  const sprint = Number(form.sprint_numero);
  const sprintOk = Number.isInteger(sprint) && sprint >= 1 && sprint <= 50;
  const tiposOk = form.tipos_casa.size > 0;
  const puedeGuardar = codigoOk && nombreOk && partidaOk && sprintOk && tiposOk;

  async function handleCrear() {
    if (!puedeGuardar) return;

    const body: SubPartidaCrear = {
      codigo: form.codigo.trim(),
      nombre: form.nombre.trim(),
      partida_id: Number(form.partida_id),
      sprint_numero: sprint,
      tipos_casa: Array.from(form.tipos_casa),
      es_critica: form.es_critica,
      descripcion: form.descripcion.trim() || null,
      activo: form.activo,
    };

    try {
      await mutation.mutateAsync(body);
      toast.success('Sub-partida creada', {
        description: `${body.codigo} — ${body.nombre}. Asigná su peso en la pantalla de Pesos.`,
      });
      onClose();
      setForm(FORM_VACIO);
    } catch (e) {
      toast.error('Error al crear', {
        description: e instanceof Error ? e.message : 'Error desconocido',
      });
    }
  }

  return (
    <Dialog open={abierto} onOpenChange={handleClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Nueva sub-partida</DialogTitle>
          <DialogDescription>
            Se crea sin peso. Luego asignás su ponderación (por sprint y por partida) en la pantalla
            de Pesos.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Código + Nombre */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label htmlFor="cr-codigo" className="mb-1.5 block text-xs">
                Código
              </Label>
              <Input
                id="cr-codigo"
                value={form.codigo}
                onChange={(e) => setForm({ ...form, codigo: e.target.value })}
                className="font-mono"
                placeholder="Ej. 3.2.5"
              />
            </div>
            <div className="col-span-2">
              <Label htmlFor="cr-nombre" className="mb-1.5 block text-xs">
                Nombre
              </Label>
              <Input
                id="cr-nombre"
                value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              />
            </div>
          </div>

          {/* Partida + Sprint */}
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Label htmlFor="cr-partida" className="mb-1.5 block text-xs">
                Partida
              </Label>
              <Select
                value={form.partida_id}
                onValueChange={(v) => setForm({ ...form, partida_id: v })}
              >
                <SelectTrigger id="cr-partida">
                  <SelectValue placeholder="Seleccioná una partida…" />
                </SelectTrigger>
                <SelectContent>
                  {partidas.map((p) => (
                    <SelectItem key={p.id} value={String(p.id)}>
                      <span className="font-mono text-xs text-muted-foreground">{p.codigo}</span>{' '}
                      {p.nombre}{' '}
                      <span className="text-xs text-muted-foreground">({p.grupo_codigo})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="cr-sprint" className="mb-1.5 block text-xs">
                Sprint
              </Label>
              <Input
                id="cr-sprint"
                type="number"
                min={1}
                max={50}
                value={form.sprint_numero}
                onChange={(e) => setForm({ ...form, sprint_numero: e.target.value })}
              />
            </div>
          </div>

          {/* Tipos de casa */}
          <div>
            <Label className="mb-2 block text-xs">Aplica a tipos de casa</Label>
            <div className="grid grid-cols-2 gap-2">
              {TIPOS_CASA.map((tc) => {
                const id = `cr-tc-${tc}`;
                return (
                  <label
                    key={tc}
                    htmlFor={id}
                    className="flex cursor-pointer items-center gap-2 rounded-md border border-border bg-white px-3 py-2 text-sm hover:bg-zinc-50"
                  >
                    <Checkbox
                      id={id}
                      checked={form.tipos_casa.has(tc)}
                      onCheckedChange={() => toggleTipoCasa(tc)}
                    />
                    <span className="font-mono">{tc}</span>
                  </label>
                );
              })}
            </div>
            {form.tipos_casa.size === 0 && (
              <p className="mt-1 text-xs text-amber-600">
                ⚠️ Elegí al menos un tipo de casa: de ahí salen las columnas donde luego asignás el
                peso.
              </p>
            )}
          </div>

          {/* Descripción */}
          <div>
            <Label htmlFor="cr-desc" className="mb-1.5 block text-xs">
              Descripción (opcional)
            </Label>
            <Textarea
              id="cr-desc"
              rows={2}
              value={form.descripcion}
              onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
            />
          </div>

          {/* Switches */}
          <div className="flex flex-col gap-3 border-t border-border pt-3">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="cr-critica" className="text-sm">
                  Crítica
                </Label>
                <p className="text-xs text-muted-foreground">
                  Bloquea el avance del sprint si está pendiente.
                </p>
              </div>
              <Switch
                id="cr-critica"
                checked={form.es_critica}
                onCheckedChange={(v) => setForm({ ...form, es_critica: v })}
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="cr-activo" className="text-sm">
                  Activa
                </Label>
                <p className="text-xs text-muted-foreground">
                  Si está inactiva no aparece para registrar avances.
                </p>
              </div>
              <Switch
                id="cr-activo"
                checked={form.activo}
                onCheckedChange={(v) => setForm({ ...form, activo: v })}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button onClick={handleCrear} disabled={!puedeGuardar || mutation.isPending}>
            {mutation.isPending ? (
              <>
                <CircleNotch
                  className="mr-2 h-4 w-4 animate-spin"
                  weight="bold"
                  aria-hidden="true"
                />
                Creando…
              </>
            ) : (
              'Crear sub-partida'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
